import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { AppConfig } from '../app.config';
import { empty, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CustomHttpParamsEncoder } from './settings/CustomHttpParamsEncoder';

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  private readonly JWT_TOKEN = 'accessToken';
  private readonly REFRESH_TOKEN = 'refreshToken';
  private readonly REFRESH_ID = 'refreshTokenId';
  private readonly TOKEN_INFO = 'token_info';

  constructor(
    private http: HttpClient,
    private config: AppConfig
  ) {
  }

  public oldLogin(name: string, username: string, password: string): Observable<any> {
    const base64 = btoa(`${username}:${password}`);
    const headers: HttpHeaders = new HttpHeaders()
      .append('Authorization', `Basic ${base64}`)
      .append("X-Requested-With", "XMLHttpRequest");
    const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamsEncoder() })
      .append('name', name)
      .append('grant_type', 'password')
      .append('username', username)
      .append('password', password);
    return this.http.post(this.config.getEndpoint('oauth/token'), null, { headers, params });
  }

  public login(name: string, username: string, password: string): Observable<any> {
    const body = {
      email: username,
      password: password
    };

    return this.http.post(this.config.getEndpoint('auth/login/credentials'), body);
  }

  public signInGoogle(): Observable<any> {

    const params: HttpParams = new HttpParams()
      .append('redirect_uri', this.config.getRedirectUri());

    return this.http.get(this.config.getEndpoint("oauth2/authorize/google"), { params });
  }

  public signInFacebook(): Observable<any> {

    const params: HttpParams = new HttpParams()
      .append('redirect_uri', this.config.getRedirectUri());

    return this.http.get(this.config.getEndpoint("oauth2/authorize/facebook"), { params });
  }


  public checkToken(): Observable<any> {
    const token = JSON.parse(localStorage.getItem('token_info'));
    const params: HttpParams = new HttpParams()
      .append('token', token[this.JWT_TOKEN]);
    return this.http.get(this.config.getEndpoint('oauth/check_token'), { params });
  }

  public refreshToken(): Observable<any> {

    const token = JSON.parse(localStorage.getItem(this.TOKEN_INFO));
    if ('refreshToken' in token) {
      const body = {
        'refreshToken': token.refreshToken,
        'refreshTokenId': token.refreshTokenId
      };

      return this.http.post(this.config.getEndpoint('auth/token/refresh'), body).pipe(
        tap((tokens) => {
          this.storeJwtToken(tokens);
        })
      );
    }
    else {
      return of(false);
    }
  }

  getJwtToken() {
    const token = JSON.parse(localStorage.getItem(this.TOKEN_INFO));

    return token ? token[this.JWT_TOKEN] : token;
  }

  getRefreshToken() {
    const token = JSON.parse(localStorage.getItem(this.TOKEN_INFO));

    return {
      refreshToken: token[this.REFRESH_TOKEN],
      refreshTokenId: token[this.REFRESH_ID]
    };
  }

  private storeJwtToken(tokens) {
    localStorage.setItem(this.TOKEN_INFO, JSON.stringify(tokens));
  }

  public loginToken(token, lng): Observable<any> {

    const params: HttpParams = new HttpParams()
      .append('language', lng);

    const body = {
      'loginToken': token.token,
      'loginTokenId': token.tokenId
    };

    return this.http.post(this.config.getEndpoint('auth/login/token'), body, { params }).pipe(
      tap((tokens) => {
        this.storeJwtToken(tokens);
      })
    );
  }

  linkSocialAccounts(token): Observable<any> {

    const body = {
      'thirdPartyAccountInfoToken': token
    };

    return this.http.post(this.config.getEndpoint('auth/third_party_accounts/link'), body);
  }

  public thirdPartyLogin(token): Observable<any> {
    const body = {
      'thirdPartyAccountInfoToken': token
    };

    return this.http.post(this.config.getEndpoint('auth/third_party_accounts/login'), body).pipe(
      tap((tokens) => {
        this.storeJwtToken(tokens);
      })
    );
  }

  public getMyInfo(token): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders()
      .append('Authorization', `Bearer ${token}`);

    const params: HttpParams = new HttpParams()
      .append('app_scheme', "SCHOOLS");

    return this.http.get(this.config.getEndpoint('member/my_info'), { headers, params });
  }

  public logout(): Observable<any> {
    const token = JSON.parse(localStorage.getItem('token_info'));
    //FIXME when we getting the token type is the first letter is lowercase, it's wrong coz when we revoke the token, the first letter in type must be in uppercase
    const headers: HttpHeaders = new HttpHeaders()
      .append('Authorization', `Bearer ${token[this.JWT_TOKEN]}`);
    const body = {
      'refreshTokenId': token[this.REFRESH_ID]
    };
    return this.http.post(this.config.getEndpoint('auth/logout/current_session'), body, { headers });
  }

  public forgotPwd(email: string, lng: string): Observable<any> {

    const body = {
      'email': email,
      'baseURL': this.config.getResetPasswordTemplate(),
      'languageEmail': lng
    };

    return this.http.post(this.config.getEndpoint('auth/password/reset/request'), body);
  }

  public resetPassword(
    newPassword: string, token: string, tokenId: string): Observable<any> {

    const body = {
      "passwordResetToken": token,
      "passwordResetTokenId": tokenId,
      "newPassword": newPassword
    }
    return this.http.post(this.config.getEndpoint('auth/password/reset/update'), body);
  }

  public updatePassword(
    newPassword: string, oldPassword: string): Observable<any> {

    const body = {
      "oldPassword": oldPassword,
      "newPassword": newPassword
    }
    return this.http.post(this.config.getEndpoint('auth/password/update'), body);
  }
}
